import React, { ChangeEvent, FC, lazy, useEffect, useMemo, useState } from 'react';
import { ReactComponent as Exclamation } from '@blink/assets/src/icons/exclamationTriangle.svg';
import { ReactComponent as OneInchIcon } from '@blink/assets/src/icons/dex/1INCH.svg';
import { ReactComponent as CurveIcon } from '@blink/assets/src/icons/dex/CURVE.svg';
import { ReactComponent as PendleIcon } from '@blink/assets/src/icons/dex/PENDLE.svg';
import { ReactComponent as WCIcon } from '@blink/assets/src/icons/walletConnectAccent.svg';
import { ButtonStroke, Input } from '@blink/components';
import { useSelector } from 'react-redux';
import { LOCAL_STORAGE, LocalStorage } from '@blink/components/src/utils';

import { useManageSectionStyle } from './ManageSection.style';

import { SelectableCard } from '@/widgets/SelectableCard';
import { Store } from '@/shared/store';
import { useWalletConnect } from '@/shared/hooks/useWalletConnect';
import { MarginAccount, Vault } from '@/shared/api/vault';
import { formatSmart, subtractFixedDecimals } from '@/shared/utils/helpers';
import useLocalFeatureFlag from '@/shared/hooks/useLocalFeatureFlag';
import { Features } from '@/shared/configs/features';

type ManageSectionProps = {
    vault: Vault;
};

const BaseManageSection: FC<ManageSectionProps> = ({ vault }) => {
    const classes = useManageSectionStyle();
    const [isLoading, setIsLoading] = useState(true);
    const [walletConnectUri, setWalletConnectUri] = useState('');

    const { connectedDapp, marginAccounts } = useSelector((state: Store) => ({
        ...(state.modals.modalProps as any),
        connectedDapp: state.wallets?.walletConnect?.connectedDapp?.name,
        marginAccounts: state.userVaults.marginAccounts,
    }));

    useEffect(() => {
        setIsLoading(marginAccounts.length === 0);
    }, [marginAccounts]);

    const marginAccountAddress = useMemo(() => {
        if (!marginAccounts || !vault?.address) return null;
        const ma = marginAccounts.find(
            (marginAccount: MarginAccount) => marginAccount.agreementAddress === vault.address,
        );
        return ma?.address || null;
    }, [marginAccounts, vault?.address]);

    const decimals = useMemo(() => {
        if (!marginAccounts || !vault?.address) return null;
        const ma = marginAccounts.find(
            (marginAccount: MarginAccount) => marginAccount.agreementAddress === vault.address,
        );
        return ma?.decimals || null;
    }, [marginAccounts, vault?.address]);

    const availableLiquidity = useMemo(() => {
        if (!vault.totalDeposited || !vault.totalBorrowed) return 'N/A';

        return subtractFixedDecimals(vault.totalDeposited, vault.totalBorrowed, decimals);
    }, [vault.totalDeposited, vault.totalBorrowed, decimals]);

    const { handlePair, disconnect } = useWalletConnect({
        marginAccountAddress,
    });

    const connectWalletByUriHandler = () => {
        if (walletConnectUri) {
            handlePair(walletConnectUri);
            LocalStorage.setItem(LOCAL_STORAGE.WALLET_CONNECT_URI, walletConnectUri);
            LocalStorage.setItem(LOCAL_STORAGE.WALLET_CONNECT_MARGIN_ADDRESS, marginAccountAddress);
        }
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => setWalletConnectUri(e.target.value);

    const handleOpenPage = (url: string) => {
        window.open(url, '_blank');
    };

    const handleDisconnect = () => {
        const topic = LocalStorage.getItem(LOCAL_STORAGE.WALLET_CONNECT_TOPIC) as string;
        if (topic) {
            disconnect(topic);
            setWalletConnectUri('');
        }
    };

    if (isLoading) {
        return null;
    }

    return (
        <div className={classes.container}>
            <div className={classes.row}>
                <div className={classes.manage}>
                    <div className={classes.formHeader}>
                        <div className={classes.information}>
                            <div className={classes.name}>Trade on external protocols</div>
                            {!connectedDapp && (
                                <div className={classes.paragraph}>
                                    Copy-paste your external wallet below to enable
                                </div>
                            )}
                        </div>
                    </div>
                    <div className={classes.formContent}>
                        {connectedDapp ? (
                            <div className={classes.wcSuccess}>
                                <div className={classes.frame}>
                                    <WCIcon />
                                    <div className={classes.message}>
                                        <div className={classes.messageTitle}>Wallet Connected</div>
                                        <div className={classes.messageText}>
                                            Your external protocol link has been successfully added.
                                            You are now ready to trade.
                                        </div>
                                    </div>
                                </div>
                                <ButtonStroke isPrimary onClick={handleDisconnect}>
                                    Disconnect
                                </ButtonStroke>
                            </div>
                        ) : (
                            <>
                                <div className={classes.form}>
                                    <div className={classes.formText}>
                                        <div className={classes.inputLabel}>
                                            Paste your pairing code here
                                        </div>
                                        <div className={classes.input}>
                                            <Input
                                                placeholder='wc:'
                                                value={walletConnectUri}
                                                onChange={handleChange}
                                            />
                                        </div>
                                    </div>
                                    <div className={classes.action}>
                                        <ButtonStroke isPrimary onClick={connectWalletByUriHandler}>
                                            Paste
                                        </ButtonStroke>
                                    </div>
                                </div>
                                <div className={classes.notification}>
                                    <Exclamation />
                                    To connect your wallet, you must first provide your wallet
                                    connect link
                                </div>
                                <div className={classes.additional}>
                                    <div className={classes.inputLabel}>
                                        Explore popular protocols you can connect to
                                    </div>
                                    <div className={classes.cards}>
                                        <SelectableCard
                                            className={classes.card}
                                            icon={<OneInchIcon />}
                                            name='1inch'
                                            onClick={() => handleOpenPage('https://app.1inch.io')}
                                        />
                                        <SelectableCard
                                            className={classes.card}
                                            icon={<CurveIcon />}
                                            name='Curve'
                                            onClick={() => handleOpenPage('https://curve.fi')}
                                        />
                                        <SelectableCard
                                            className={classes.card}
                                            icon={<PendleIcon />}
                                            name='Pendle'
                                            onClick={() =>
                                                handleOpenPage('https://app.pendle.finance')
                                            }
                                        />
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
                <div className={classes.info}>
                    <div className={classes.header}>
                        <div className={classes.item}>
                            <div className={classes.label}>Available Liquidity:</div>
                            <div className={classes.value}>{availableLiquidity}</div>
                        </div>
                        <div className={classes.item}>
                            <div className={classes.label}>Total Supply</div>
                            <div className={classes.value}>
                                {formatSmart(vault.totalDeposited!)}
                            </div>
                        </div>
                    </div>
                    <div className={classes.content}>
                        <div className={classes.title}>Vault Info</div>
                        <div className={classes.description}>{vault.description}</div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const LazyManageSectionModal = lazy(() =>
    import('./v2/ManageSection').then((module) => ({
        default: module.ManageSection,
    })),
);

export const ManageSection: FC<ManageSectionProps> = ({ vault }) => {
    const isNewWalletConnect = useLocalFeatureFlag(Features.NewWalletConnect, true);

    if (!isNewWalletConnect) {
        return <BaseManageSection vault={vault} />;
    }

    return (
        <React.Suspense fallback={null}>
            <LazyManageSectionModal vault={vault} />
        </React.Suspense>
    );
};
